import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useWebSocket } from "@/lib/ws";
import { SignalsTable } from "@/components/SignalsTable";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

const TFs = ["all", "M1", "M5", "M15", "M30", "H1", "H4", "D1"];
const STATUSES = ["all", "pending", "consumed", "expired", "superseded"];
const PATTERNS = ["all", "FVG", "BOS", "CHoCH", "LiquiditySweep"];

export default function Signals() {
  const [params, setParams] = useSearchParams();
  const [rawSignals, setRawSignals] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [tfFilter, setTfFilter] = useState("all");
  const [patternFilter, setPatternFilter] = useState("all");
  const [symbolFilter, setSymbolFilter] = useState(params.get("symbol") || "");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/signals?limit=200&status=${statusFilter}`);
      setRawSignals(r.data.signals || []);
    } catch (e) {
      toast.error("Failed to load signals");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const s = params.get("symbol");
    if (s !== null && s !== symbolFilter) setSymbolFilter(s);
  }, [params, symbolFilter]);

  useWebSocket((msg) => {
    if (msg.type === "signal.new") setRawSignals((prev) => [msg.data, ...prev]);
    else if (msg.type === "signal.consumed") {
      setRawSignals((prev) => prev.map((s) => s.id === msg.data.id
        ? { ...s, status: "consumed", consumed_at: msg.data.consumed_at } : s));
    } else if (msg.type === "signal.expired") {
      setRawSignals((prev) => prev.map((s) => s.id === msg.data.id ? { ...s, status: "expired" } : s));
    } else if (msg.type === "signal.superseded") {
      setRawSignals((prev) => prev.map((s) => s.id === msg.data.id
        ? { ...s, status: "superseded", superseded_by: msg.data.superseded_by } : s));
    }
  });

  const filtered = rawSignals.filter((s) => {
    if (tfFilter !== "all" && s.timeframe !== tfFilter) return false;
    if (patternFilter !== "all" && s.pattern_type !== patternFilter) return false;
    if (symbolFilter && !s.symbol.toLowerCase().includes(symbolFilter.toLowerCase())) return false;
    return true;
  });

  const symbols = Array.from(new Set(rawSignals.map((s) => s.symbol)));

  const setSymbol = (val) => {
    setSymbolFilter(val);
    const next = new URLSearchParams(params);
    if (val) next.set("symbol", val); else next.delete("symbol");
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="signals-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="vx-label">Signals</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-1">All Signals</h1>
        </div>
        <button
          onClick={load}
          className="h-9 px-3 rounded-md border border-[#1f2937] hover:bg-[#1f2937] text-xs font-mono flex items-center gap-2 transition-colors"
          data-testid="refresh-signals-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="vx-card p-4 flex flex-wrap items-end gap-4" data-testid="signals-filters">
        <div className="min-w-[140px]">
          <div className="vx-label mb-1.5">Symbol</div>
          <input
            type="text"
            placeholder="EURUSD…"
            list="symbols-datalist"
            value={symbolFilter}
            onChange={(e) => setSymbol(e.target.value)}
            className="vx-input h-9 text-xs"
            data-testid="filter-symbol"
          />
          <datalist id="symbols-datalist">
            {symbols.map((s) => <option key={s} value={s} />)}
          </datalist>
        </div>
        <div>
          <div className="vx-label mb-1.5">Timeframe</div>
          <select
            value={tfFilter}
            onChange={(e) => setTfFilter(e.target.value)}
            className="vx-input h-9 text-xs w-28"
            data-testid="filter-timeframe"
          >
            {TFs.map((t) => <option key={t} value={t}>{t === "all" ? "All" : t}</option>)}
          </select>
        </div>
        <div>
          <div className="vx-label mb-1.5">Pattern</div>
          <select
            value={patternFilter}
            onChange={(e) => setPatternFilter(e.target.value)}
            className="vx-input h-9 text-xs w-36"
            data-testid="filter-pattern"
          >
            {PATTERNS.map((p) => <option key={p} value={p}>{p === "all" ? "All Patterns" : p}</option>)}
          </select>
        </div>
        <div>
          <div className="vx-label mb-1.5">Status</div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="vx-input h-9 text-xs w-36"
            data-testid="filter-status"
          >
            {STATUSES.map((s) => <option key={s} value={s}>{s === "all" ? "All" : s}</option>)}
          </select>
        </div>
        <div className="ml-auto text-[11px] text-[#6b7280] font-mono">
          Showing {filtered.length} of {rawSignals.length}
        </div>
      </div>

      <SignalsTable signals={filtered} emptyLabel="No signals match filters." />
    </div>
  );
}
